import { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles, alpha } from '@material-ui/core';
import UserCount from '../../../../userCount';
import UserCountBadge from '../../../../userCountBadge';
import PlaceholderButton from '../../../../placeholderButton';
import DotsMenu from '../../../../dotsMenu';
import ActionButton from '../../../../actionButton';
import { ACTION_BUTTON_TYPES } from '../../../../actionButton/config';
import Extensions from './extensions';
import DraggableScroll from '../../../../draggableScroll';
import ScrollIndicators from '../../../../scrollIndicators';
import TextBoxWithTooltip from '../../../../textBoxWithTooltip';
import { ReactComponent as BellIcon } from '../../../../../../assets/icons/notifications_none.svg';
import { ABSENCE_FIELD_NAMES } from '../../../../../../constants/absence';
import { useDraggableScrollPosition } from '../../../../../../utility/hooks';
import { getUserStatusLabel } from '../../../../../../utility/uiUtils';
import { formatDate } from '../../../../../../utility/dateUtils';
import { getMenuItems } from '../../config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    backgroundColor: ({ color }) => color,
    boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.07)}`,
    borderRadius: 16,
    display: 'flex',
    height: 210,
    maxWidth: '100%',
  },
  rootNoAdditionalSettings: {
    height: 60,
  },
  info: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(4),
    height: '100%',
    width: 370,
    flexShrink: 0,
  },
  infoHeaderWrapper: {
    marginBottom: spacing(4),
  },
  infoHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(2),
  },
  title: {
    marginTop: spacing(0.75),
  },
  icon: {
    flexShrink: 0,
  },
  actionsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
  },
  reportButton: {
    width: 24,
    height: 24,
    padding: 0,
  },
  countBadge: {
    backgroundColor: primary.bluish8,
  },
  userCount: {
    flexShrink: 0,
    backgroundColor: 'unset',
    cursor: 'default',
    opacity: 0.2,
  },
  daysCountLabel: {
    color: primary.bluish4,
  },
  rolloverWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 'auto',
  },
  rolloverIcon: {
    flexShrink: 0,
    marginBottom: spacing(0.5),
    marginRight: spacing(1),
  },
  infoExtension: {
    display: 'flex',
    alignItems: 'center',
  },
  extensions: {
    display: 'flex',
    width: '100%',
    maxWidth: 'calc(100% - 370px)',
    position: 'relative',
  },
  scrollWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  itemsWrapper: {
    display: 'flex',
    width: 'auto',
  },
  arrow: {
    top: 90,
  },
  addExtensionButton: {
    backgroundColor: primary.bluish9,
    border: `1px dashed ${primary.bluish5}`,
    borderRadius: 16,
    width: 300,
    minWidth: 300,
    minHeight: 194,
    margin: spacing(0, 2),
    transition: 'box-shadow .3s ease',
    '&:hover': {
      border: `1px dashed ${primary.bluish5}`,
      boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.12)}`,
    },
  },
}));

const Absence = ({
  translations,
  absence,
  canViewAbsenceReport,
  setUsersCount,
  onEditAbsence,
  onEditAbsenceTemplate = undefined,
  onDeleteAbsence,
  onGetAbsenceUsers,
  onRemoveAbsenceUsers,
  onAddExtension,
  onEditExtension,
  onDeleteExtension,
  onGetExtensionUsers,
  onRemoveExtensionUsers,
  canAccessUserProfileCheck,
  onGoToProfilePage,
  onGoToAbsenceReport,
  onAddAbsenceUsers,
  onAddExtensionUsers,
}) => {
  const scrollRef = useRef(null);
  const itemsRef = useRef(null);

  const {
    NAME,
    COLOR,
    HAS_DAYS_ENABLED,
    NUMBER_OF_DAYS,
    USER_COUNT,
    HAS_ROLLOVER,
    ROLLOVER_DATE,
    HAS_EXTENSIONS,
    EXTENSIONS,
  } = ABSENCE_FIELD_NAMES;
  const { absenceIcon: Icon, accrualLabel } = absence;
  const classes = useStyles({ color: absence[COLOR] });
  const hasDaysEnabled = absence[HAS_DAYS_ENABLED];
  const hasAbsenceUsers = absence[USER_COUNT] !== 0;
  const hasSetUsers = setUsersCount - absence[USER_COUNT];

  const [
    activeScroll,
    handleChangeScrollPosition,
    onScrollToRight,
    ,
    onScrollToStart,
  ] = useDraggableScrollPosition(scrollRef, itemsRef);

  const timeOffMenuItems = getMenuItems(
    translations.menuItemLabels,
    absence,
    onEditAbsence,
    onDeleteAbsence,
    undefined,
    onEditAbsenceTemplate,
    false
  );

  return (
    <div
      className={classNames(classes.root, {
        [classes.rootNoAdditionalSettings]: !hasDaysEnabled,
      })}
    >
      <div className={classes.info}>
        <div
          className={classNames({
            [classes.infoHeaderWrapper]: hasDaysEnabled,
          })}
        >
          <div className={classes.infoHeaderTitle}>
            <Icon className={classes.icon} />
            <TextBoxWithTooltip
              className={classes.title}
              variant="h5"
              text={absence[NAME]}
            />
            <div className={classes.actionsWrapper}>
              {hasAbsenceUsers || hasSetUsers ? (
                <UserCount
                  customUserCountBadgeClassName={classes.countBadge}
                  actionButtonLabel={translations.remove}
                  addButtonLabel={translations.add}
                  userCount={absence[USER_COUNT]}
                  shouldDisablePortal={false}
                  getUsers={onGetAbsenceUsers}
                  onTooltipAction={onRemoveAbsenceUsers}
                  onTooltipAdd={onAddAbsenceUsers}
                  canAccessUserProfileCheck={canAccessUserProfileCheck}
                  goToUserProfile={onGoToProfilePage}
                  shouldHandleTypeLabel
                  handleTypeLabel={user =>
                    getUserStatusLabel(translations.deactivated, user)
                  }
                  hasActionButton
                  hasAddButton
                  tooltipButtonProps={{
                    type: 'removeDarkRoundedOutlined',
                    disabled: !hasAbsenceUsers,
                  }}
                  addTooltipButtonProps={{
                    disabled: !hasSetUsers,
                  }}
                />
              ) : (
                <UserCountBadge
                  className={classes.userCount}
                  userCount={absence[USER_COUNT]}
                />
              )}
              {canViewAbsenceReport && (
                <ActionButton
                  className={classes.reportButton}
                  iconButtonClass={classes.reportButton}
                  type={ACTION_BUTTON_TYPES.REPORT}
                  tooltipText={translations.reportTooltip}
                  disabledTooltipText={translations.reportTooltipDisabled}
                  isDisabled={!hasAbsenceUsers}
                  onClickHandler={onGoToAbsenceReport}
                />
              )}
              <DotsMenu menuItems={timeOffMenuItems} isVerticalMenu />
            </div>
          </div>
        </div>
        {hasDaysEnabled && (
          <div>
            <Typography className={classes.daysCountLabel} variant="body2">
              {translations.daysCountLabel}
            </Typography>
            <Typography variant="h1">
              {`${absence[NUMBER_OF_DAYS]} ${
                absence[NUMBER_OF_DAYS] === 1
                  ? translations.day
                  : translations.days
              }`}
            </Typography>
            {absence[HAS_DAYS_ENABLED] && (
              <Typography variant="body2">{accrualLabel}</Typography>
            )}
          </div>
        )}
        {absence[HAS_ROLLOVER] && (
          <div className={classes.rolloverWrapper}>
            <BellIcon className={classes.rolloverIcon} />
            <Typography variant="body2">{`${
              translations.rollOverExpiryDate
            } ${formatDate(absence[ROLLOVER_DATE], 'MMM dd')}`}</Typography>
          </div>
        )}
      </div>
      {absence[HAS_EXTENSIONS] && (
        <div className={classes.extensions}>
          <DraggableScroll
            ref={el => {
              scrollRef.current = el;
            }}
            className={classes.scrollWrapper}
            onEndScroll={handleChangeScrollPosition}
          >
            <div ref={itemsRef} className={classes.itemsWrapper}>
              <div className={classes.itemsWrapper}>
                <Extensions
                  translations={translations}
                  extensions={absence[EXTENSIONS]}
                  typeUsersCount={absence[USER_COUNT]}
                  onEdit={onEditExtension}
                  onDelete={onDeleteExtension}
                  onGetUsers={onGetExtensionUsers}
                  onRemoveUsers={onRemoveExtensionUsers}
                  canAccessUserProfileCheck={canAccessUserProfileCheck}
                  onGoToProfilePage={onGoToProfilePage}
                  onAddExtensionUsers={onAddExtensionUsers}
                />
                <PlaceholderButton
                  className={classes.addExtensionButton}
                  label={translations.addTypeExtension}
                  onAdd={onAddExtension(absence)()}
                />
              </div>
            </div>
          </DraggableScroll>
          <ScrollIndicators
            arrowLeftClass={classes.arrow}
            arrowRightClass={classes.arrow}
            activeScroll={activeScroll}
            onScrollToRight={onScrollToRight}
            onScrollToStart={onScrollToStart}
          />
        </div>
      )}
    </div>
  );
};

Absence.propTypes = {
  translations: PropTypes.object.isRequired,
  absence: PropTypes.object.isRequired,
  setUsersCount: PropTypes.number.isRequired,
  onEditAbsence: PropTypes.func.isRequired,
  onEditAbsenceTemplate: PropTypes.func,
  onDeleteAbsence: PropTypes.func.isRequired,
  onGetAbsenceUsers: PropTypes.func.isRequired,
  onRemoveAbsenceUsers: PropTypes.func.isRequired,
  onAddExtension: PropTypes.func.isRequired,
  onEditExtension: PropTypes.func.isRequired,
  onDeleteExtension: PropTypes.func.isRequired,
  onGetExtensionUsers: PropTypes.func.isRequired,
  onRemoveExtensionUsers: PropTypes.func.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  onGoToProfilePage: PropTypes.func.isRequired,
  onGoToAbsenceReport: PropTypes.func.isRequired,
  onAddAbsenceUsers: PropTypes.func.isRequired,
  onAddExtensionUsers: PropTypes.func.isRequired,
};

export default Absence;
